<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'

const roles = [
  'Full-Stack Developer',
  'Hardware Tinkerer',
  'AI Builder',
  'IoT Engineer',
  'Open Source Contributor',
]

const floatingBadges = [
  { label: 'Vue.js', left: '6%', top: '12%', delay: '0s', duration: '5s' },
  { label: 'RISC-V', left: '85%', top: '15%', delay: '0.8s', duration: '6s' },
  { label: 'AI', left: '3%', top: '35%', delay: '1.5s', duration: '4.5s' },
  { label: 'TypeScript', left: '88%', top: '32%', delay: '1.2s', duration: '6.5s' },
  { label: 'Three.js', left: '5%', top: '60%', delay: '0.6s', duration: '4.8s' },
  { label: 'IoT', left: '90%', top: '58%', delay: '0.4s', duration: '5.5s' },
  { label: 'Nuxt', left: '8%', top: '82%', delay: '2s', duration: '5s' },
  { label: 'Docker', left: '86%', top: '85%', delay: '1.8s', duration: '5.2s' },
]

const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

const sectionRef = useTemplateRef<HTMLElement>('section')
const contentRef = useTemplateRef<HTMLElement>('content')
const titleRef = useTemplateRef<HTMLElement>('title')
const badgesRef = useTemplateRef<HTMLElement>('badges')
const indicatorRef = useTemplateRef<HTMLElement>('indicator')
const payoffRef = useTemplateRef<HTMLElement>('payoff')

const titleShimmer = shallowRef(false)

// Scroll-driven dive progress consumed by the NeuralMesh render loop —
// ScrollTrigger only writes this value; the canvas reads it per frame.
const diveProgress = shallowRef(0)

let ctx: { revert: () => void } | null = null
let mm: { revert: () => void } | null = null

onMounted(async () => {
  // ?pcb2d — board layout debug view: hide every overlay, skip all
  // choreography, let CircuitBoard's top-down camera own the frame
  if (new URLSearchParams(window.location.search).has('pcb2d')) {
    const overlays = sectionRef.value?.querySelectorAll<HTMLElement>('.fx-vignette, .fx-grain') ?? []
    for (const el of [contentRef.value, badgesRef.value, indicatorRef.value, payoffRef.value, ...overlays]) {
      if (el) el.style.display = 'none'
    }
    return
  }

  if (prefersReducedMotion.value) {
    titleShimmer.value = true
    return
  }

  const [{ gsap }, { SplitText }, { ScrollTrigger }] = await Promise.all([
    import('gsap'),
    import('gsap/SplitText'),
    import('gsap/ScrollTrigger'),
  ])
  gsap.registerPlugin(SplitText, ScrollTrigger)

  const section = sectionRef.value
  const title = titleRef.value
  if (!section || !title) return

  ctx = gsap.context(() => {
    // Take over the title from its CSS fallback animation: split into
    // masked chars, hide them, then reveal — all before the next paint.
    const split = new SplitText(title, { type: 'chars', mask: 'chars' })
    gsap.set(split.chars, { yPercent: 120 })
    title.style.animation = 'none'
    title.style.opacity = '1'

    gsap.to(split.chars, {
      yPercent: 0,
      duration: 1,
      ease: 'power4.out',
      stagger: 0.045,
      delay: 0.5,
      onComplete: () => {
        split.revert()
        titleShimmer.value = true
      },
    })
  }, section)

  mm = gsap.matchMedia()

  // Desktop: pin the hero for a 250vh runway and choreograph the flythrough.
  // Phases live on one scrubbed timeline (positions are fractions of 1):
  //   0.00–0.30  intro content parallaxes out, badges lead
  //   0.25–0.95  camera flies across the board and dives onto the CPU die
  //   0.42–0.74  payoff statement holds mid-flight over the dark board
  //   0.74–1.00  the etched die alone carries the closing message
  mm.add('(min-width: 768px)', () => {
    const dive = { value: 0 }
    const tl = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: section,
        start: 'top 64px',
        end: '+=250%',
        pin: true,
        scrub: 0.8,
        anticipatePin: 1,
      },
    })

    tl.to(indicatorRef.value, { autoAlpha: 0, duration: 0.05 }, 0)
      .to(badgesRef.value, { yPercent: -40, autoAlpha: 0, duration: 0.26 }, 0.02)
      .to(contentRef.value, { yPercent: -22, autoAlpha: 0, duration: 0.3 }, 0.05)
      .to(dive, {
        value: 1,
        duration: 0.7,
        onUpdate: () => { diveProgress.value = dive.value },
      }, 0.25)
      .fromTo(payoffRef.value, { autoAlpha: 0, y: 60, scale: 0.95 }, {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.14,
        ease: 'power1.out',
      }, 0.42)
      .to(payoffRef.value, { autoAlpha: 0, yPercent: -10, duration: 0.1 }, 0.72)
  })

  // Mobile: no pin, no dive — just the lightweight layered exit
  mm.add('(max-width: 767.98px)', () => {
    gsap.to(contentRef.value, {
      yPercent: -25,
      autoAlpha: 0,
      ease: 'none',
      scrollTrigger: { trigger: section, start: 'top top', end: '75% top', scrub: true },
    })
    gsap.to(indicatorRef.value, {
      autoAlpha: 0,
      ease: 'none',
      scrollTrigger: { trigger: section, start: 'top top', end: '12% top', scrub: true },
    })
  })
})

onUnmounted(() => {
  mm?.revert()
  ctx?.revert()
})
</script>

<template>
  <section ref="section" class="relative flex min-h-screen items-center justify-center overflow-hidden">
    <CircuitBoard :progress="diveProgress" />

    <!-- Cinematic finish: vignette + film grain over the 3D scene -->
    <div class="fx-vignette pointer-events-none absolute inset-0 z-[3]" />
    <div class="fx-grain pointer-events-none absolute inset-0 z-[3]" />

    <!-- Hero radial gradient (light mode contrast) -->
    <div class="absolute inset-0 z-[1]" style="background: var(--hero-gradient)" />

    <!-- Gradient overlay for text readability -->
    <div class="absolute inset-0 z-[2] bg-gradient-to-b from-[var(--color-bg)]/40 via-transparent to-[var(--color-bg)]/80" />

    <!-- Floating tech badges (full-width, relative to section).
         Outer div is the GSAP scroll-exit target; inner div owns the CSS
         entrance animation — they must be separate elements. -->
    <div ref="badges" class="pointer-events-none absolute inset-0 z-10 hidden sm:block">
      <div class="absolute inset-0" data-hero="badges">
        <span
          v-for="(badge, i) in floatingBadges"
          :key="badge.label"
          class="absolute rounded-full border border-deep-blue/30 bg-deep-blue/8 px-3 py-1 text-xs font-medium text-deep-blue/70 backdrop-blur-sm dark:border-amber/15 dark:bg-amber/5 dark:text-amber/60"
          :style="{
            left: badge.left,
            top: badge.top,
            animation: `float-badge ${badge.duration} ease-in-out ${badge.delay} infinite`,
            opacity: 0.4 + (i % 3) * 0.15,
          }"
        >
          {{ badge.label }}
        </span>
      </div>
    </div>

    <div ref="content" class="relative z-10 mx-auto max-w-4xl px-4 text-center">
      <!-- content-hugging scrim: guarantees text contrast over the 3D scene
           in both themes, fades at the edges so it never reads as a panel -->
      <div class="absolute -inset-x-40 -inset-y-12 -z-10 bg-[radial-gradient(ellipse_at_center,var(--color-bg)_0%,transparent_70%)] opacity-70" />
      <p data-hero="subtitle" class="mb-4 h-6 text-sm font-medium tracking-widest text-[#b8751a] uppercase dark:text-[var(--color-amber)]">
        <TypedText :strings="roles" :pause-duration="2500" />
      </p>

      <!-- Name -->
      <h1
        ref="title"
        data-hero="title"
        class="mb-6 text-5xl font-bold tracking-tight text-deep-blue sm:text-7xl dark:text-amber"
        :class="{ 'hero-gradient-text': titleShimmer }"
      >
        Shakir Abdulrahim
      </h1>

      <div data-hero="desc" class="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-[#2c4356] [text-shadow:0_1px_16px_rgba(245,242,237,0.95)] dark:text-[#7a9bb5] dark:[text-shadow:0_2px_20px_rgba(4,7,14,0.85)]">
        Building at the intersection of software and hardware — from web applications and Legal Tech solutions to RISC-V microcontrollers and IoT systems.
      </div>
      <div data-hero="cta" class="flex flex-col items-center justify-center gap-4 sm:flex-row">
        <MagneticWrap>
          <UButton
            to="/projects"
            size="xl"
            class="bg-[var(--color-amber)] px-8 text-[var(--color-dark-base)] shadow-[0_0_30px_rgba(250,185,91,0.2)] transition-shadow duration-300 hover:bg-[var(--color-amber)]/90 hover:shadow-[0_0_40px_rgba(250,185,91,0.35)]"
          >
            View Projects
          </UButton>
        </MagneticWrap>
        <MagneticWrap>
          <UButton
            to="/contact"
            size="xl"
            variant="outline"
            class="border-[var(--color-steel-blue)]/50 bg-[var(--color-bg)]/40 px-8 text-[var(--color-text)] backdrop-blur-sm transition-all duration-300 hover:border-[var(--color-amber)] hover:text-[var(--color-amber)] hover:shadow-[0_0_20px_rgba(250,185,91,0.1)]"
          >
            Get in Touch
          </UButton>
        </MagneticWrap>
      </div>
    </div>

    <!-- Dive payoff — revealed mid-flight through the mesh (desktop pin only) -->
    <!-- held in the upper "sky" band so it never collides with the chip below -->
    <div ref="payoff" class="pointer-events-none absolute inset-0 z-10 hidden items-start justify-center pt-[13vh] opacity-0 md:flex">
      <div class="mx-auto max-w-3xl px-4 text-center [text-shadow:0_2px_28px_rgba(4,7,14,0.85)]">
        <p class="mb-6 font-mono text-sm tracking-widest text-[var(--color-amber)] uppercase">
          // the full stack, literally
        </p>
        <p class="text-4xl font-bold text-[var(--color-text)] sm:text-6xl">
          From pixels <span class="text-[var(--color-amber)]">to silicon.</span>
        </p>
        <p class="mt-6 text-lg leading-relaxed text-[var(--color-text-muted)]">
          Web apps, AI agents, and the hardware they run on.
        </p>
      </div>
    </div>

    <!-- Scroll indicator — mouse icon -->
    <div ref="indicator" class="absolute bottom-16 left-1/2 z-10 -translate-x-1/2">
      <div class="flex h-8 w-5 items-start justify-center rounded-full border border-[var(--color-steel-blue)]/30 p-1">
        <div class="h-2 w-1 animate-bounce rounded-full bg-[var(--color-amber)]" />
      </div>
    </div>
  </section>
</template>
